import { useCallback, useRef, useState } from 'react';
import {
  getPromptVariants,
  getPromptVariantById,
  createPromptVariant,
  updatePromptVariant,
  deletePromptVariant,
  executePromptVariant,
} from 'services/promptVariantService';

export default function usePromptVariants() {
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [executeResult, setExecuteResult] = useState(null);
  const [executeError, setExecuteError] = useState(null);
  const abortControllerRef = useRef(null);

  const fetchVariants = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const result = await getPromptVariants(controller.signal);
      const list = Array.isArray(result) ? result : result?.data ?? [];
      setVariants(list);
      return list;
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      setError(err);
      console.error('Error loading prompt variants:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchVariantById = useCallback(async (variantId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getPromptVariantById(variantId);
      return result;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const submitVariant = useCallback(async (payload) => {
    setSaving(true);
    setError(null);
    try {
      const isUpdate = !!payload.id;
      const result = isUpdate
        ? await updatePromptVariant(payload)
        : await createPromptVariant(payload);
      return { ok: true, data: result };
    } catch (err) {
      setError(err);
      return { ok: false, error: err };
    } finally {
      setSaving(false);
    }
  }, []);

  const removeVariant = useCallback(async (variantId) => {
    setSaving(true);
    setError(null);
    try {
      await deletePromptVariant(variantId);
      setVariants((prev) => prev.filter((v) => v.id !== variantId));
      return { ok: true };
    } catch (err) {
      setError(err);
      return { ok: false, error: err };
    } finally {
      setSaving(false);
    }
  }, []);

  const execute = useCallback(async (variantId, variables = {}) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setExecuting(true);
    setExecuteError(null);
    setExecuteResult(null);

    try {
      const result = await executePromptVariant(
        { variantId, variables },
        controller.signal
      );
      setExecuteResult(result);
      return { ok: true, data: result };
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      setExecuteError(err);
      return { ok: false, error: err };
    } finally {
      setExecuting(false);
    }
  }, []);

  const resetExecution = useCallback(() => {
    setExecuteResult(null);
    setExecuteError(null);
  }, []);

  return {
    variants,
    loading,
    error,
    saving,
    executing,
    executeResult,
    executeError,
    fetchVariants,
    fetchVariantById,
    submitVariant,
    removeVariant,
    execute,
    resetExecution,
  };
}
